import { API, graphqlOperation } from "aws-amplify";
import {
  listProductLibProcessJobs,
  getProductLibProcessJob,
} from "../graphql/queries";
import {
  createProductLibProcessJob,
  updateProductLibProcessJob,
  deleteProductLibProcessJob,
} from "../graphql/mutations";
import {
  ProductLibProcessJob,
  CreateProductLibProcessJobInput,
  UpdateProductLibProcessJobInput,
  ModelProductLibProcessJobFilterInput,
  ProcessJobType,
  ProcessJobStatus,
} from "../API";

// Enum constants for easy access
export const JobType = {
  PRODUCT_KNOWLEDGE: "PRODUCT_KNOWLEDGE" as ProcessJobType,
} as const;

export const JobStatus = {
  PROCESSING: "PROCESSING" as ProcessJobStatus,
  COMPLETED: "COMPLETED" as ProcessJobStatus,
  FAILED: "FAILED" as ProcessJobStatus,
} as const;

// IsActive constants
export const IsActive = {
  YES: "Y",
  NO: "N",
} as const;

// List all process jobs with optional filter
export const fetchProcessJobs = async (
  filter?: ModelProductLibProcessJobFilterInput,
  limit: number = 100
): Promise<ProductLibProcessJob[]> => {
  let nextToken: string | null = null;
  let allItems: ProductLibProcessJob[] = [];

  do {
    const result: any = await API.graphql(
      graphqlOperation(listProductLibProcessJobs, {
        filter,
        limit,
        nextToken,
      })
    );

    const items = result.data.listProductLibProcessJobs.items;
    allItems = [...allItems, ...items];
    nextToken = result.data.listProductLibProcessJobs.nextToken;
  } while (nextToken !== null);

  return allItems;
};

// Get single process job by ID
export const fetchProcessJobById = async (
  id: string
): Promise<ProductLibProcessJob | null> => {
  const result: any = await API.graphql(
    graphqlOperation(getProductLibProcessJob, { id })
  );

  return result.data.getProductLibProcessJob;
};

// Create new process job
export const createProcessJob = async (
  input: CreateProductLibProcessJobInput
): Promise<ProductLibProcessJob | null> => {
  const result: any = await API.graphql(
    graphqlOperation(createProductLibProcessJob, { input })
  );

  return result.data.createProductLibProcessJob;
};

// Update existing process job
export const updateProcessJob = async (
  input: UpdateProductLibProcessJobInput
): Promise<ProductLibProcessJob | null> => {
  const result: any = await API.graphql(
    graphqlOperation(updateProductLibProcessJob, { input })
  );

  return result.data.updateProductLibProcessJob;
};

// Delete process job by ID
export const deleteProcessJob = async (
  id: string
): Promise<ProductLibProcessJob | null> => {
  const result: any = await API.graphql(
    graphqlOperation(deleteProductLibProcessJob, { input: { id } })
  );

  return result.data.deleteProductLibProcessJob;
};

// Fetch process jobs by status
export const fetchProcessJobsByStatus = async (
  status: ProcessJobStatus
): Promise<ProductLibProcessJob[]> => {
  return fetchProcessJobs({ status: { eq: status } });
};

// Fetch process jobs by job type
export const fetchProcessJobsByJobType = async (
  jobType: ProcessJobType
): Promise<ProductLibProcessJob[]> => {
  return fetchProcessJobs({ jobType: { eq: jobType } });
};

// Fetch process jobs by isActive
export const fetchProcessJobsByIsActive = async (
  isActive: string
): Promise<ProductLibProcessJob[]> => {
  return fetchProcessJobs({ isActive: { eq: isActive } });
};

// Fetch active process jobs by job type
export const fetchActiveProcessJobsByJobType = async (
  jobType: ProcessJobType
): Promise<ProductLibProcessJob[]> => {
  return fetchProcessJobs({
    jobType: { eq: jobType },
    isActive: { eq: IsActive.YES },
  });
};
